// Components/Common/DashboardKit/ColumnFilterRow.js
//
// R41 — per-column filter row, matching the Epicor "Customer" page UX:
// one filter input directly beneath each column header.
//
// R46 — text inputs now commit on Enter or Tab/blur (not on every
// keystroke). Previously a 350ms debounce hit the server while the user
// was still typing (e.g. typing "85761" fired "8", "85", "857", "8576",
// "85761" requests, often showing "no rows match" before the user had
// finished). Now the user types freely; pressing Enter or moving focus
// commits the value to the parent filter state.
//
// Dropdowns and boolean selects still commit immediately on change —
// they're single-click interactions so debounce/commit doesn't apply.

import React, { useState, useEffect } from "react";
import { Input } from "reactstrap";

// R46 — text input with deferred commit (Enter/blur).
// Round 61 (Mafia UX) — optional `live` mode: commits as the user types
// (250ms debounce) for CLIENT-side filtered tables, where instant feedback
// is the expected behavior (per data-table UX guidance: filters should give
// immediate visual feedback). Server-side tables keep the default deferred
// commit so we never spam the API mid-keystroke. Enter/blur still commit
// immediately in both modes.
const FilterTextInput = ({ value, onCommit, placeholder, live = false }) => {
    // Local state holds what the user is typing. Parent state only updates
    // when the user explicitly commits (Enter / blur).
    const [local, setLocal] = useState(value || "");
    const debounceRef = React.useRef(null);

    // If the parent clears the filter externally (e.g. tab switch), sync.
    // We intentionally depend only on `value` — not on `local`, which
    // would cause every keystroke to reset itself. R49: removed the
    // eslint-disable directive that referenced a rule (react-hooks/
    // exhaustive-deps) not registered in this project's eslint config;
    // the directive itself was the lint error.
    useEffect(() => {
        if ((value || "") !== local) {
            setLocal(value || "");
        }
    }, [value]);

    const commit = () => {
        const next = local;
        if (next !== (value || "")) {
            onCommit(next);
        }
    };

    // Round 61 — live mode: debounce-commit while typing. Cleared on unmount.
    useEffect(() => {
        if (!live) return undefined;
        if (local === (value || "")) return undefined;
        debounceRef.current = setTimeout(() => onCommit(local), 250);
        return () => clearTimeout(debounceRef.current);
        
    }, [local, live]);

    return (
        <Input
            type="search"
            bsSize="sm"
            placeholder={placeholder || "Filter…"}
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                    if (debounceRef.current) clearTimeout(debounceRef.current);
                    commit();
                } else if (e.key === "Escape") {
                    // Restore committed value, no commit.
                    if (debounceRef.current) clearTimeout(debounceRef.current);
                    setLocal(value || "");
                }
            }}
            onBlur={() => {
                if (debounceRef.current) clearTimeout(debounceRef.current);
                commit();
            }}
        />
    );
};

const ColumnFilterRow = ({ columns, filters, onChange, live = false }) => {
    const setOne = (id, val) => onChange({ ...(filters || {}), [id]: val });
    return (
        <tr className="column-filter-row" style={{ background: "var(--ssw-surface-3, #fafbfc)" }}>
            {columns.map((c) => {
                if (c.type === "none") {
                    return <th key={c.id} style={{ padding: "4px 6px" }} />;
                }
                if (c.type === "select") {
                    return (
                        <th key={c.id} style={{ padding: "4px 6px" }}>
                            <Input
                                type="select"
                                bsSize="sm"
                                value={(filters && filters[c.id]) || ""}
                                onChange={(e) => setOne(c.id, e.target.value)}
                            >
                                <option value="">All</option>
                                {(c.options || []).map((opt) => {
                                    const v = typeof opt === "string" ? opt : opt.value;
                                    const l = typeof opt === "string" ? opt : opt.label;
                                    return <option key={v} value={v}>{l}</option>;
                                })}
                            </Input>
                        </th>
                    );
                }
                if (c.type === "boolean") {
                    return (
                        <th key={c.id} style={{ padding: "4px 6px" }}>
                            <Input
                                type="select"
                                bsSize="sm"
                                value={(filters && filters[c.id]) || ""}
                                onChange={(e) => setOne(c.id, e.target.value)}
                            >
                                <option value="">All</option>
                                <option value="true">Yes</option>
                                <option value="false">No</option>
                            </Input>
                        </th>
                    );
                }
                // text (default) — R46 deferred-commit input (R61: live opt-in)
                return (
                    <th key={c.id} style={{ padding: "4px 6px" }}>
                        <FilterTextInput
                            value={(filters && filters[c.id]) || ""}
                            onCommit={(val) => setOne(c.id, val)}
                            placeholder={c.placeholder}
                            live={live}
                        />
                    </th>
                );
            })}
        </tr>
    );
};

// Apply the active filters to a rows array. Skips columns with type "none"
// and skips empty filter values. Text filters do substring (case-insensitive),
// boolean filters do exact match against truthiness, select filters do
// exact (case-insensitive) match against the selected value.
export const applyColumnFilters = (rows, columns, filters) => {
    if (!rows || !filters) return rows || [];
    const active = (columns || [])
        .filter((c) => c.type !== "none" && filters[c.id] != null && filters[c.id] !== "")
        .map((c) => ({ ...c, _q: filters[c.id] }));
    if (active.length === 0) return rows;
    return rows.filter((row) =>
        active.every((c) => {
            const v = c.get ? c.get(row) : row[c.id];
            if (c.type === "boolean") {
                const wantTrue = String(c._q).toLowerCase() === "true";
                return !!v === wantTrue;
            }
            if (c.type === "select") {
                return String(v ?? "").toLowerCase() === String(c._q).toLowerCase();
            }
            // text — substring, case-insensitive
            return String(v ?? "").toLowerCase().includes(String(c._q).toLowerCase());
        })
    );
};

export default ColumnFilterRow;
